// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useResizable.ts
 * @input Resize configuration (defaultSize, minSizePx, maxSizePx, collapsible, snaps)
 * @output Hook return: size, isCollapsed, collapse/expand/resize methods, props for handle
 * @position Public hook; consumed by layout components via `resizable` prop
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * Shared config shape for any component that integrates built-in resize
 * (e.g. SideNav `resizable` prop). Provides a simplified API surface
 * over the full ResizableRegionConfig.
 */

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_MIN = 50;
const DEFAULT_COLLAPSED_SIZE = 40;
const STORAGE_PREFIX = 'astryx-resizable:';

// =============================================================================
// Helpers
// =============================================================================

function clampSize(size, min, max, snaps) {
  const clamped = Math.min(max, Math.max(min, size));

  // When snap points are defined, always snap to the nearest one.
  // No intermediate positions — the panel can only rest at snap values.
  if (snaps.length > 0) {
    let nearest = snaps[0];
    let nearestDist = Math.abs(clamped - nearest);
    for (let i = 1; i < snaps.length; i++) {
      const dist = Math.abs(clamped - snaps[i]);
      if (dist < nearestDist) {
        nearest = snaps[i];
        nearestDist = dist;
      }
    }
    return Math.min(max, Math.max(min, nearest));
  }
  return clamped;
}
function loadPersistedSize(key) {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw != null) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'number') {
        return parsed;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}
function persistSize(key, size) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(size));
  } catch {
    /* ignore */
  }
}
function resolveDefaultSize(defaultSize) {
  if (defaultSize == null) {
    return 250;
  }
  if (typeof defaultSize === 'number') {
    return defaultSize;
  }
  if (defaultSize.endsWith('%')) {
    const pct = parseFloat(defaultSize);
    if (!isNaN(pct)) {
      const approx = typeof window !== 'undefined' ? window.innerWidth : 1200;
      return Math.round(pct / 100 * approx);
    }
  }
  return 250;
}

// =============================================================================
// Single-region hook
// =============================================================================

function useSingleResizable(config) {
  const {
    defaultSize,
    minSizePx = DEFAULT_MIN,
    maxSizePx = Infinity,
    collapsible = false,
    collapsedSize = DEFAULT_COLLAPSED_SIZE,
    snaps = [],
    autoSaveId,
    onSizeChange,
    onCollapseChange
  } = config;
  const resolvedDefault = resolveDefaultSize(defaultSize);
  const persisted = autoSaveId ? loadPersistedSize(autoSaveId) : null;
  const initial = persisted ?? resolvedDefault;
  const [size, setSize] = useState(() => clampSize(initial, minSizePx, maxSizePx, snaps));
  const [isCollapsed, setIsCollapsed] = useState(() => persisted === 0 && collapsible);
  const preCollapseSizeRef = useRef(size);
  const dragStartSizeRef = useRef(size);
  useEffect(() => {
    if (autoSaveId) {
      persistSize(autoSaveId, isCollapsed ? 0 : size);
    }
  }, [size, isCollapsed, autoSaveId]);
  const collapse = useCallback(() => {
    if (!collapsible) {
      return;
    }
    preCollapseSizeRef.current = size;
    setIsCollapsed(true);
    setSize(0);
    onCollapseChange?.(true);
    onSizeChange?.(0);
  }, [collapsible, size, onCollapseChange, onSizeChange]);
  const expand = useCallback(() => {
    setIsCollapsed(false);
    const restored = preCollapseSizeRef.current || resolvedDefault;
    const newSize = clampSize(restored, minSizePx, maxSizePx, snaps);
    setSize(newSize);
    onCollapseChange?.(false);
    onSizeChange?.(newSize);
  }, [resolvedDefault, minSizePx, maxSizePx, snaps, onCollapseChange, onSizeChange]);
  const resize = useCallback(newSize => {
    const clamped = clampSize(newSize, minSizePx, maxSizePx, snaps);
    setSize(clamped);
    setIsCollapsed(false);
    onSizeChange?.(clamped);
  }, [minSizePx, maxSizePx, snaps, onSizeChange]);
  const onResizeStart = useCallback(() => {
    dragStartSizeRef.current = isCollapsed ? 0 : size;
  }, [size, isCollapsed]);
  const onResizeMove = useCallback(delta => {
    const raw = dragStartSizeRef.current + delta;
    if (collapsible && raw < collapsedSize) {
      if (!isCollapsed) {
        preCollapseSizeRef.current = size;
        onCollapseChange?.(true);
      }
      setIsCollapsed(true);
      setSize(0);
      onSizeChange?.(0);
      return;
    }
    if (isCollapsed && raw >= collapsedSize) {
      setIsCollapsed(false);
      onCollapseChange?.(false);
    }
    const clamped = clampSize(raw, minSizePx, maxSizePx, snaps);
    setSize(clamped);
    onSizeChange?.(clamped);
  }, [collapsible, collapsedSize, isCollapsed, size, minSizePx, maxSizePx, snaps, onSizeChange, onCollapseChange]);
  const onResizeEnd = useCallback(() => {
    // Sizes already committed during move
  }, []);
  const props = {
    _size: isCollapsed ? 0 : size,
    _isCollapsed: isCollapsed,
    _onResizeStart: onResizeStart,
    _onResizeMove: onResizeMove,
    _onResizeEnd: onResizeEnd,
    _minSizePx: minSizePx,
    _maxSizePx: maxSizePx,
    _snaps: snaps,
    _collapsedSize: collapsedSize,
    _collapsible: collapsible,
    _isResizableProps: true
  };
  return {
    size: isCollapsed ? 0 : size,
    isCollapsed,
    collapse,
    expand,
    resize,
    props
  };
}

// =============================================================================
// Multi-region hook
// =============================================================================

/**
 * Multi-region hook — delegates to individual useSingleResizable calls.
 * Region keys must be stable across renders (same count and order).
 * This is enforced by the caller providing a static `regions` object.
 */
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix -- calls useSingleResizable in .map()
function useMultiResizable(config) {
  const {
    regions,
    autoSaveId
  } = config;

  // Stable key order — callers must not change region keys between renders.
  // Using Object.keys is safe here because the regions object shape is static.
  const regionEntries = Object.entries(regions);

  // Call hooks unconditionally in stable order (same count every render).

  const regionResults = regionEntries.map(([key, regionConfig]) =>
  // eslint-disable-next-line @eslint-react/rules-of-hooks -- region count is stable (documented contract)
  useSingleResizable({
    ...regionConfig,
    autoSaveId: autoSaveId ? `${autoSaveId}:${key}` : undefined
  }));
  const result = {};
  regionEntries.forEach(([key], i) => {
    result[key] = regionResults[i];
  });
  return result;
}

// =============================================================================
// Public API
// =============================================================================

export function useResizable(config) {
  if ('regions' in config) {
    // eslint-disable-next-line @eslint-react/rules-of-hooks, react-compiler/react-compiler -- branch is determined by call-site type (stable per call site)
    return useMultiResizable(config);
  }
  // eslint-disable-next-line @eslint-react/rules-of-hooks, react-compiler/react-compiler -- branch is determined by call-site type (stable per call site)
  return useSingleResizable(config);
}